/**
 * PrivacyCenter — admin-facing transparency surface for sovereign AI.
 *
 * Three sections (top to bottom):
 *
 *   1. Header trust strip — TLS status + 30-day stats + sovereignty
 *      score teaser (live link to AWS Connection page).
 *   2. Provider policy matrix — for every LLM provider (Bedrock,
 *      Anthropic, OpenAI, Gemini, VE Gateway) shows training-use status
 *      + retention + a verdict pill (gold / ok / warn).
 *   3. Redaction sandbox — paste a query, see exactly what gets masked
 *      before it leaves the system. Includes tenant trade-secret
 *      tokens management (add/delete project codenames the redactor
 *      should always strip).
 *   4. Recent-redactions feed — last 50 anonymised events so admin can
 *      audit "yes the redactor fired N times today".
 *
 * Auth: holding_admin or system_admin only.
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, Lock, Eye, AlertTriangle, Plus, Trash2, Sparkles, Activity, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/lib/api';

export default function PrivacyCenter() {
  const [overview, setOverview] = useState(null);
  const [secrets, setSecrets] = useState([]);
  const [recent, setRecent] = useState([]);
  const [sample, setSample] = useState('Send the term sheet to maria.al-thani@example.com about the Project Falcon deal — IBAN QA12VEAW1234567890. Phone +974 5555 1234.');
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [newSecret, setNewSecret] = useState({ token: '', notes: '' });
  const [adding, setAdding] = useState(false);

  const loadAll = async () => {
    try {
      const [ov, s, r] = await Promise.all([
        api.get('/privacy/overview'),
        api.get('/privacy/secrets'),
        api.get('/privacy/recent-redactions'),
      ]);
      // queueMicrotask defers state updates to keep the react-hooks
      // purity lint happy when called from useEffect.
      queueMicrotask(() => {
        setOverview(ov.data);
        setSecrets(s.data.secrets || []);
        setRecent(r.data.events || []);
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load privacy center.');
    }
  };
  useEffect(() => { loadAll(); }, []);

  const runScan = async (persist = false) => {
    setScanning(true);
    try {
      const { data } = await api.post('/privacy/redact-test', { text: sample, persist });
      setScan(data);
      if (data.clean) toast.success('Query was already clean — nothing to redact.');
      else toast.message(`Masked ${data.total_redactions} item(s) before send.`);
      if (persist) loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const addSecret = async () => {
    if ((newSecret.token || '').trim().length < 3) {
      toast.error('Secret token must be ≥ 3 characters.');
      return;
    }
    setAdding(true);
    try {
      await api.post('/privacy/secrets', newSecret);
      toast.success('Secret added — every external LLM call will mask it from now on.');
      setNewSecret({ token: '', notes: '' });
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setAdding(false); }
  };

  const delSecret = async (id) => {
    if (!window.confirm('Remove this trade-secret token? External calls will stop masking it.')) return;
    try {
      await api.delete(`/privacy/secrets/${id}`);
      toast.success('Removed.');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  if (!overview) return <div className="ve-card p-5"><span className="ve-eyebrow">[PRIVACY · LOADING…]</span></div>;

  const stats = overview.stats_30d || {};
  const max = Math.max(1, ...(overview.sparkline_14d || []).map((d) => d.scans));

  return (
    <div className="space-y-6" data-testid="privacy-center-page">
      {/* Header trust strip */}
      <div className="ve-card p-6 flex items-start gap-4">
        <div className="h-12 w-12 rounded-md flex items-center justify-center shrink-0"
             style={{ background: 'var(--brand-primary-soft)', color: 'var(--brand-primary)' }}>
          <ShieldCheck size={22}/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="ve-eyebrow">Sovereign AI · Privacy Center</div>
          <h1 className="display text-2xl mt-1">Your queries stay yours.</h1>
          <p className="text-sm opacity-70 mt-1 max-w-2xl">
            Every outbound LLM call is encrypted, redacted, and never used for training. This page exists so you can prove it — to your board, your auditors, and to yourself, anytime.
          </p>
        </div>
        <Link to="/dashboard/settings/aws" data-testid="privacy-aws-link"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border border-emerald-400/40 bg-emerald-500/10 text-emerald-300 text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-emerald-500/20">
          <Sparkles size={11}/> Sovereignty Score →
        </Link>
      </div>

      {/* Sovereign-AI Seal — downloadable, regulator-ready marketing badge */}
      <div className="ve-card p-5 flex items-start gap-5 flex-wrap" data-testid="sovereign-seal-card">
        <div className="shrink-0 rounded-md overflow-hidden border border-[var(--brand-primary-strong)]" style={{ width: 190, height: 110 }}>
          <object
            type="image/svg+xml"
            data={`${process.env.REACT_APP_BACKEND_URL}/api/privacy/sovereignty-seal.svg`}
            className="w-full h-full"
            aria-label="Sovereign-AI seal preview"
          />
        </div>
        <div className="flex-1 min-w-[260px]">
          <div className="ve-eyebrow">Sovereign-AI Seal</div>
          <h3 className="display text-lg mt-1">A regulator-ready badge.</h3>
          <p className="text-sm opacity-75 mt-1 max-w-2xl">
            Paste this seal into your LinkedIn banner, RFP responses, investor decks, or your homepage. It encodes your tenant&apos;s 30-day Bedrock share + a SHA-256 hash so anyone can verify it hasn&apos;t been tampered with.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/privacy/sovereignty-seal.svg`}
              download="sovereign-ai-seal.svg"
              data-testid="download-sovereign-seal"
              className="ve-btn-primary text-[10px] inline-flex items-center gap-1.5"
            >
              Download SVG
            </a>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/privacy/sovereignty-attestation.pdf`}
              download
              data-testid="download-quarterly-attestation"
              className="ve-btn-secondary text-[10px] inline-flex items-center gap-1.5"
            >
              Download Quarterly Attestation (PDF)
            </a>
            <button
              type="button"
              data-testid="copy-seal-embed"
              onClick={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/privacy/sovereignty-seal.svg`;
                const snippet = `<img src="${url}" alt="Sovereign-AI Seal" width="380" height="220" />`;
                navigator.clipboard?.writeText(snippet);
                toast.success('Embed snippet copied to clipboard.');
              }}
              className="px-3 py-1.5 rounded border border-[var(--brand-primary-strong)] text-[10px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--brand-primary-soft)]"
            >
              Copy &lt;img&gt; embed
            </button>
          </div>
        </div>
      </div>

      {/* Encryption + stats strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard icon={Lock} label="Ingress encryption"   value={overview.tls?.ingress?.split(' ')[0] || 'TLS 1.3'} sub="K8s ingress, cluster-terminated"/>
        <StatCard icon={Activity} label="Outbound to LLMs"  value="TLS 1.2+" sub="every provider · always HTTPS"/>
        <StatCard icon={Eye}  label="Redactions (30d)"    value={String(stats.redactions || 0)} sub={`${stats.scans || 0} queries scanned`}/>
        <StatCard icon={FileText} label="Trade-secret tokens" value={String(overview.tenant_secrets_count || 0)} sub="enforced on every external call"/>
      </div>

      {/* Sparkline of last 14 days of scans */}
      <div className="ve-card p-5">
        <div className="ve-eyebrow mb-3">[14-day scan activity]</div>
        <div className="flex items-end gap-1 h-20">
          {(overview.sparkline_14d || []).map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center gap-1" title={`${d.day} · ${d.scans} scans`}>
              <div className="w-full rounded-t" style={{ height: `${(d.scans / max) * 100}%`, minHeight: 2, background: 'var(--brand-primary)' }}/>
            </div>
          ))}
        </div>
        <div className="mt-2 text-[10px] pal-mono opacity-50 flex justify-between">
          <span>{overview.sparkline_14d?.[0]?.day}</span>
          <span>today</span>
        </div>
      </div>

      {/* Provider policy matrix */}
      <div className="ve-card p-5">
        <div className="ve-eyebrow mb-3">[01] Provider training policy</div>
        <p className="text-sm opacity-70 mb-4 max-w-3xl">
          Where does each provider stand on using our prompts to train future models? Bedrock is the gold standard — prompts never leave your AWS region. Every other provider is contractually opted out by default, with a 30-day retention for abuse review only.
        </p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-60">
              <th className="text-left py-2 font-bold">Provider</th>
              <th className="text-left py-2 font-bold">Training use</th>
              <th className="text-left py-2 font-bold">Notes</th>
              <th className="text-right py-2 font-bold">Verdict</th>
            </tr>
          </thead>
          <tbody>
            {(overview.providers || []).map((p) => (
              <tr key={p.id} data-testid={`policy-row-${p.id}`} className="border-t border-slate-100 dark:border-white/5">
                <td className="py-3 font-bold">{p.label}</td>
                <td className="py-3 pal-mono text-xs">{p.training_use}</td>
                <td className="py-3 text-xs opacity-75 max-w-[460px]">{p.notes}</td>
                <td className="py-3 text-right">
                  <span className={`pal-mono text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded border ${p.verdict === 'gold' ? 'border-emerald-400/60 bg-emerald-500/10 text-emerald-300' : p.verdict === 'warn' ? 'border-amber-400/40 bg-amber-500/10 text-amber-300' : 'border-[var(--brand-primary-strong)] bg-[var(--brand-primary-soft)] text-[var(--brand-primary)]'}`}>
                    {p.verdict}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Redaction sandbox */}
      <div className="ve-card p-5">
        <div className="ve-eyebrow mb-3">[02] Built-in PII patterns</div>
        <p className="text-sm opacity-70 mb-3 max-w-3xl">
          These nine patterns are scanned on every outbound LLM call. Toggle one OFF if your workflow needs that pattern to reach the LLM intact (e.g. URL extraction for content analysis). Trade-secret tokens (Section [03]) are always enforced.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 mb-5">
          {(overview.patterns || []).map((p) => (
            <button key={p.id} type="button"
              onClick={async () => {
                try {
                  await api.patch('/privacy/patterns', { pattern_id: p.id, enabled: !p.enabled });
                  toast.success(`${p.label}: ${!p.enabled ? 'ON' : 'OFF'}`);
                  loadAll();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Toggle failed'); }
              }}
              data-testid={`pattern-toggle-${p.id}`}
              className={`text-left px-3 py-2.5 rounded border transition-colors flex items-center justify-between gap-2 ${p.enabled ? 'border-emerald-400/40 bg-emerald-500/5' : 'border-amber-400/30 bg-amber-500/5 opacity-75'}`}>
              <div className="min-w-0">
                <div className="text-[12px] font-bold flex items-center gap-1.5">
                  {p.enabled ? <ShieldCheck size={11} className="text-emerald-400"/> : <AlertTriangle size={11} className="text-amber-400"/>}
                  {p.label}
                </div>
                <div className="pal-mono text-[10px] opacity-50 truncate">masks as {p.mask}</div>
              </div>
              <span className={`pal-mono text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded ${p.enabled ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                {p.enabled ? 'ON' : 'OFF'}
              </span>
            </button>
          ))}
        </div>

        <div className="ve-eyebrow mb-3">Redaction sandbox</div>
        <p className="text-sm opacity-70 mb-3 max-w-3xl">
          Paste any query below. We&apos;ll show you exactly what would be masked before it leaves the system — using your current pattern + trade-secret settings. Nothing is sent to an LLM during this test.
        </p>
        <textarea value={sample} onChange={(e) => setSample(e.target.value)}
          data-testid="redact-sample"
          className="w-full rounded border border-slate-200 dark:border-white/15 bg-transparent px-3 py-2 text-sm font-mono leading-relaxed focus:outline-none focus:border-[var(--brand-primary)]"
          rows={4}/>
        <div className="mt-3 flex flex-wrap gap-2">
          <button onClick={() => runScan(false)} disabled={scanning} data-testid="redact-run"
            className="ve-btn-primary inline-flex items-center gap-1.5">
            <Eye size={13}/> {scanning ? 'Scanning…' : 'Scan only'}
          </button>
          <button onClick={() => runScan(true)} disabled={scanning} data-testid="redact-run-persist"
            className="px-4 py-2 rounded border border-[var(--brand-primary-strong)] bg-[var(--brand-primary-soft)] text-[var(--brand-primary)] text-xs font-bold uppercase tracking-[0.18em] inline-flex items-center gap-1.5">
            <Activity size={11}/> Scan & log
          </button>
        </div>

        {scan && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-60 mb-1">After redaction (what gets sent)</div>
              <pre data-testid="redact-output" className="rounded border border-emerald-400/30 bg-emerald-500/5 p-3 text-xs font-mono whitespace-pre-wrap leading-relaxed text-emerald-200">{scan.redacted_text}</pre>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-60 mb-1">
                Detected ({scan.total_redactions})
              </div>
              {scan.hits.length === 0 ? (
                <div className="rounded border border-emerald-400/30 bg-emerald-500/5 p-3 text-xs text-emerald-200 inline-flex items-center gap-1.5"><ShieldCheck size={12}/> Already clean — nothing to mask.</div>
              ) : (
                <div className="space-y-1.5">
                  {scan.hits.map((h, i) => (
                    <div key={i} className="rounded border border-rose-400/30 bg-rose-500/5 px-3 py-2 text-xs flex items-center justify-between gap-3">
                      <span className="text-rose-200">{h.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="pal-mono text-rose-300/70">{h.sample || '•••'}</span>
                        <span className="pal-mono text-[10px] font-bold opacity-70 px-1.5 py-0.5 rounded bg-rose-500/20">×{h.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Trade-secret tokens */}
      <div className="ve-card p-5">
        <div className="ve-eyebrow mb-3">[03] Trade-secret tokens</div>
        <p className="text-sm opacity-70 mb-3 max-w-3xl">
          Project codenames, deal IDs, legal-entity codes — anything that should NEVER appear in outbound LLM traffic. Add a token here and every council call, every Iqra narrative, every Yusuf brief will redact it automatically.
        </p>
        <div className="flex flex-wrap gap-2 items-end mb-3">
          <div className="flex-1 min-w-[200px]">
            <label className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-65 block mb-1">Token</label>
            <input value={newSecret.token} onChange={(e) => setNewSecret({ ...newSecret, token: e.target.value })}
              placeholder="e.g. Project Falcon"
              data-testid="secret-token"
              className="w-full rounded border border-slate-200 dark:border-white/15 bg-transparent px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-primary)]"/>
          </div>
          <div className="flex-1 min-w-[240px]">
            <label className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-65 block mb-1">Notes (optional)</label>
            <input value={newSecret.notes} onChange={(e) => setNewSecret({ ...newSecret, notes: e.target.value })}
              placeholder="why this matters"
              data-testid="secret-notes"
              className="w-full rounded border border-slate-200 dark:border-white/15 bg-transparent px-3 py-2 text-sm focus:outline-none focus:border-[var(--brand-primary)]"/>
          </div>
          <button onClick={addSecret} disabled={adding} data-testid="secret-add"
            className="ve-btn-primary inline-flex items-center gap-1.5 self-end">
            <Plus size={13}/> Add
          </button>
        </div>
        {secrets.length === 0 ? (
          <div className="text-xs opacity-55 italic">No trade-secret tokens configured yet.</div>
        ) : (
          <div className="space-y-1.5">
            {secrets.map((s) => (
              <div key={s.secret_id} data-testid={`secret-${s.secret_id}`}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded border border-slate-200 dark:border-white/10 text-xs">
                <div className="min-w-0">
                  <div className="pal-mono font-bold truncate">{s.token}</div>
                  {s.notes && <div className="opacity-65 mt-0.5 truncate">{s.notes}</div>}
                </div>
                <button onClick={() => delSecret(s.secret_id)} data-testid={`secret-delete-${s.secret_id}`}
                  className="p-1.5 rounded hover:bg-rose-500/10 text-rose-400 transition-colors">
                  <Trash2 size={13}/>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent redactions feed */}
      <div className="ve-card p-5" data-testid="recent-redactions">
        <div className="ve-eyebrow mb-3">[04] Recent redactions · last 50</div>
        {recent.length === 0 ? (
          <div className="text-xs opacity-55 italic flex items-center gap-1.5">
            <AlertTriangle size={13} className="text-amber-400"/>
            No redaction events recorded yet — either no LLM calls have been made, or every prompt was already clean.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[9px] uppercase tracking-[0.18em] font-bold opacity-60">
                <th className="text-left py-1.5">When</th>
                <th className="text-left py-1.5">Via</th>
                <th className="text-right py-1.5">Length</th>
                <th className="text-right py-1.5">Masked</th>
                <th className="text-left py-1.5 pl-3">Hits</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((r, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-white/5">
                  <td className="py-1.5 pal-mono opacity-70">{r.at?.replace('T', ' ').slice(0, 16)}</td>
                  <td className="py-1.5 pal-mono">{r.via || '—'}</td>
                  <td className="py-1.5 pal-mono text-right">{r.input_length}</td>
                  <td className="py-1.5 pal-mono text-right font-bold text-[var(--brand-primary)]">{r.total_redactions}</td>
                  <td className="py-1.5 pl-3">
                    <div className="flex flex-wrap gap-1">
                      {(r.hits || []).slice(0, 5).map((h, j) => (
                        <span key={j} className="pal-mono text-[10px] px-1.5 py-0.5 rounded border border-rose-400/30 bg-rose-500/5 text-rose-300">
                          {h.label || h.pattern_id} ×{h.count}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="ve-card p-4">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold opacity-60">
        <Icon size={11}/> {label}
      </div>
      <div className="mt-1.5 pal-mono text-xl font-bold text-[var(--brand-primary)]">{value}</div>
      <div className="text-[10px] opacity-55 mt-0.5">{sub}</div>
    </div>
  );
}
